import { css } from '@emotion/react';
import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { NakedButton, PopoverMenu, Text, useFormField, usePopoverMenu } from '@frontend/design-system';
import { FilterableCheckboxList } from '../../../components/filterable-checkbox-list';

type MultiSelectTriggerProps = {
  label: string;
  selectedCount: number;
  totalCount: number;
  leadingIcon: IconName;
  getTriggerProps: ReturnType<typeof usePopoverMenu>['getTriggerProps'];
  isOpen: boolean;
  fullWidth?: boolean;
};

const MultiSelectTrigger = ({
  label,
  selectedCount,
  totalCount,
  leadingIcon,
  getTriggerProps,
  isOpen,
  fullWidth,
}: MultiSelectTriggerProps) => {
  const allSelected = selectedCount === totalCount;
  return (
    <NakedButton css={[styles.trigger, fullWidth && styles.triggerFullWidth]} {...getTriggerProps()}>
      <Icon name={leadingIcon} size={16} color='light' />
      <Text size='medium' weight='bold' color='default'>
        {label}
      </Text>
      <div css={styles.rightGroup}>
        {!allSelected && (
          <Text as='span' size='small' weight='light' color='subdued'>
            {selectedCount}
          </Text>
        )}
        <Icon
          name='caret-down-tiny'
          size={8}
          color='light'
          css={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </div>
    </NakedButton>
  );
};

type ChecklistPopoverProps = {
  label: string;
  fieldName: string;
  leadingIcon: IconName;
  options: { value: string; label: string }[];
  checklistField: ReturnType<typeof useFormField<'checklist'>>;
  fullWidth?: boolean;
};

export const ChecklistPopover = ({
  label,
  fieldName,
  leadingIcon,
  options,
  checklistField,
  fullWidth,
}: ChecklistPopoverProps) => {
  const { getTriggerProps, getMenuProps } = usePopoverMenu({ placement: 'bottom-start' });
  const isOpen = getMenuProps().isOpen;
  const selectedCount = checklistField.value.length;
  const totalCount = options.length;

  return (
    <>
      <MultiSelectTrigger
        label={label}
        selectedCount={selectedCount === 0 ? totalCount : selectedCount}
        totalCount={totalCount}
        leadingIcon={leadingIcon}
        getTriggerProps={getTriggerProps}
        isOpen={isOpen}
        fullWidth={fullWidth}
      />
      <PopoverMenu {...getMenuProps()} css={styles.popover}>
        <FilterableCheckboxList fieldName={fieldName} list={options} checkListField={checklistField} />
      </PopoverMenu>
    </>
  );
};

const styles = {
  trigger: css`
    display: flex;
    align-items: center;
    white-space: nowrap;
    gap: ${theme.spacing(0.5)};
    padding: ${theme.spacing(0.5, 1.5)};
    border-radius: ${theme.borderRadius.small};
    background-color: ${theme.colors.white};
    border: 1px solid ${theme.colors.neutral20};
  `,
  triggerFullWidth: css`
    width: 100%;
  `,
  rightGroup: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    margin-left: auto;
  `,
  popover: css`
    padding: ${theme.spacing(1)};
    min-width: 220px;
    max-height: 360px;
    overflow-y: auto;
  `,
};
